/**
 * OIDC Configuration for Keycloak (D-029)
 *
 * Configures the OpenID Connect client that the React SPA uses to authenticate
 * users via Keycloak. This is the bridge between our React app and Keycloak's
 * OIDC endpoints.
 *
 * ─── How OIDC Discovery Works ───────────────────────────────────────────────
 * The `authority` URL points to the Keycloak realm. The OIDC library appends
 * `/.well-known/openid-configuration` to discover all endpoints automatically:
 *   - Authorization endpoint (where the user logs in)
 *   - Token endpoint (where auth codes are exchanged for tokens)
 *   - JWKS endpoint (public keys for JWT validation)
 *   - Userinfo endpoint (user profile data)
 *
 * ─── Why PKCE (S256) ────────────────────────────────────────────────────────
 * Public clients (SPAs) can't keep a client_secret — the browser source is
 * readable by anyone. PKCE (Proof Key for Code Exchange) replaces the secret
 * with a per-request cryptographic challenge:
 *   1. SPA generates random `code_verifier` + hashes it → `code_challenge`
 *   2. Auth request sends `code_challenge` to Keycloak
 *   3. Token request sends `code_verifier` — Keycloak re-hashes and compares
 * An intercepted auth code is useless without the verifier (which never leaves
 * the browser). This is the OAuth 2.1 standard for all public clients.
 *
 * ─── JS Parallel ────────────────────────────────────────────────────────────
 * If you've used Firebase Auth or Auth0 in JS, this is the same pattern:
 * configure a provider, wrap the app, get tokens from context. The difference
 * is we control the identity server (Keycloak) instead of using a SaaS.
 */

import type { AuthProviderProps } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";

// Environment variables injected by Vite at build time.
// These are prefixed with VITE_ so Vite includes them in the client bundle.
// Non-VITE_ env vars are NOT exposed to the browser (security boundary).
const KEYCLOAK_URL =
  import.meta.env.VITE_KEYCLOAK_URL || "http://localhost:8180";
const REALM = import.meta.env.VITE_KEYCLOAK_REALM || "crucio";
const CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || "crucio-web";
const REDIRECT_URI =
  import.meta.env.VITE_REDIRECT_URI || "http://localhost:5173";

/**
 * OIDC configuration passed to `react-oidc-context`'s AuthProvider.
 *
 * The library handles the full Authorization Code + PKCE flow:
 *   login() → redirect to Keycloak → user authenticates → redirect back with
 *   auth code → library exchanges code for tokens → stores in sessionStorage
 */
export const oidcConfig: AuthProviderProps = {
  // The OIDC "authority" is the realm URL. The library appends
  // /.well-known/openid-configuration to discover all endpoints.
  authority: `${KEYCLOAK_URL}/realms/${REALM}`,

  client_id: CLIENT_ID,

  // Where Keycloak sends the user after login. Must match one of the
  // redirectUris in crucio-realm.json — Keycloak rejects mismatches.
  redirect_uri: `${REDIRECT_URI}/callback`,

  // Where Keycloak sends the hidden iframe during silent token renewal.
  // Points to a lightweight HTML page (~500 bytes) that relays the auth
  // response back to the parent window via postMessage — no React, no
  // bundler, no router. Without this, the iframe loads the full React app
  // via /callback, wasting ~500KB and risking the 10s renewal timeout.
  // The redirect URI is already covered by the wildcard in crucio-realm.json.
  silent_redirect_uri: `${REDIRECT_URI}/silent-renew.html`,

  // Where to send the user after logout.
  post_logout_redirect_uri: REDIRECT_URI,

  // Authorization Code flow (response_type: "code") with PKCE.
  // The library auto-generates code_verifier + code_challenge.
  response_type: "code",

  // OIDC scopes — what claims we want in the token:
  //   openid: required for OIDC (sub claim)
  //   profile: name, preferred_username
  //   email: email, email_verified
  scope: "openid profile email",

  // Silent token renewal: refreshes the access token in a hidden iframe
  // before it expires. The user never sees a re-login prompt during a session.
  // Uses the refresh_token grant under the hood.
  automaticSilentRenew: true,

  // Trigger renewal 2 minutes before token expiry (default is 60 seconds).
  // With 30-min tokens (TF.5.5), this fires at t=28min — plenty of buffer.
  // WHY 120s not 300s: if the value equals or exceeds the token lifespan,
  // oidc-client-ts considers the token "expiring" immediately on issue and
  // enters a continuous silent-renew loop. 120s is safely below the minimum
  // lifespan we'd ever configure (currently 300s for e2e client).
  accessTokenExpiringNotificationTimeInSeconds: 120,

  // sessionStorage (not localStorage) for token storage.
  // WHY: Tokens are cleared when the browser tab closes — defense in depth.
  // localStorage persists across tabs/sessions, which widens the attack surface
  // if XSS occurs. sessionStorage scopes tokens to the current tab.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
};
